import { useEffect, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { Button } from '@mui/material'
import { OverlayState } from '@/hooks/useOverlay'
import { useUpdateProject } from '@/service-library/hooks/projects'
import { Project } from '@/types/projects'
import { Dialog, DialogContent, DialogFooter } from '@/components/dialog'
import { FormTextField, PixyDocsForm } from '@/components/forms'

type FormValues = {
  description: string
}

type EditProjectDescriptionDialogProps = {
  overlay: OverlayState
  project: Project
  updateProject: ReturnType<typeof useUpdateProject>['updateProject']
}

export default function EditProjectDescriptionDialog({
  overlay,
  project,
  updateProject,
}: EditProjectDescriptionDialogProps) {
  const inputRef = useRef<HTMLInputElement>(null)

  const methods = useForm<FormValues>({
    defaultValues: {
      description: project.description || '',
    },
  })

  const { formState, reset } = methods

  function onSubmit(values: FormValues) {
    updateProject({
      ...project,
      description: values.description.trim(),
    })
    overlay.close()
  }

  useEffect(() => {
    if (!overlay.isOpen) {
      reset({
        description: project.description,
      })
    }
  }, [project.description, overlay.isOpen, reset])

  useEffect(() => {
    if (overlay.isOpen) {
      setTimeout(() => {
        inputRef.current?.focus()
        inputRef.current?.setSelectionRange(
          inputRef.current.value.length,
          inputRef.current.value.length,
        )
      }, 0)
    }
  }, [overlay.isOpen])

  return (
    <Dialog title="Edit Project Description" {...overlay} maxWidth="sm">
      <PixyDocsForm methods={methods} onSubmit={onSubmit}>
        <DialogContent>
          <FormTextField
            name="description"
            label="Description"
            fullWidth
            multiline
            rows={4}
            ref={inputRef}
            placeholder="Describe the type of document that goes into this project"
            helperText="This should describe the kind of document that goes into the
            project, not what the project does when processing the document."
          />
        </DialogContent>
        <DialogFooter>
          <Button variant="text" onClick={overlay.close}>
            Cancel
          </Button>
          <Button type="submit" disabled={!formState.isDirty}>
            Save
          </Button>
        </DialogFooter>
      </PixyDocsForm>
    </Dialog>
  )
}
